/**
 * Which PCR plate each file came from, for pre-filling an order's pools. Plates are read off the file
 * names (well positions, Illumina sample numbers) within each sequencing run from the headers.
 */
import { fileRuns } from './sequencingRunDetection.ts'
import { fastqBaseName } from '../fileNames.ts'

export const PLATE_WELLS = 96

/** `run` when the file's run is already known (e.g. read once and stored); otherwise it is taken from `headers`. */
export type PlateFile = { name: string; headers?: readonly string[]; run?: string }

export type WellPosition = { row: number; column: number }


const SAMPLE_NUMBER = /_S(\d+)(?:_L\d{3})?_R[12](?:_\d{3})?$/

/** The `S12` of an Illumina `bcl2fastq` name (`x_S12_L001_R1_001`): the sample's row in the run's samplesheet. */
export const illuminaSampleNumber = (name: string): number | null => {
  const match = SAMPLE_NUMBER.exec(fastqBaseName(name))
  return match ? Number(match[1]) : null
}

const WELL_TOKEN = /^([A-H])(0?[1-9]|1[0-2])$/i
/* a well glued to a control name, as in `negPCR2H03` */
const GLUED_WELL = /(?<=[a-z0-9])([A-H])(0[1-9]|1[0-2])(?=$|[_.-])/

/** The 96-well position a name carries as its own token (`_A01_`, `-H12-`), or glued to a control name. */
export const wellPosition = (name: string): WellPosition | null => {
  const base = fastqBaseName(name)
  const tokens = base.split(/[_.-]/).filter((token) => WELL_TOKEN.test(token))
  const found =
    tokens.length === 1 ? WELL_TOKEN.exec(tokens[0]) : tokens.length === 0 ? GLUED_WELL.exec(base.replace(/_S\d+_.*$/, '')) : null
  if (!found) {
    return null
  }
  return { row: 'ABCDEFGH'.indexOf(found[1].toUpperCase()), column: Number(found[2]) }
}

const naturalKey = (name: string): (string | number)[] =>
  fastqBaseName(name)
    .toLowerCase()
    .split(/(\d+)/)
    .filter(Boolean)
    .map((part) => (/^\d+$/.test(part) ? Number(part) : part))

const compareNatural = (a: string, b: string): number => {
  const ka = naturalKey(a)
  const kb = naturalKey(b)
  for (let i = 0; i < Math.min(ka.length, kb.length); i++) {
    const x = ka[i]
    const y = kb[i]
    if (x === y) {
      continue
    }
    if (typeof x === 'number' && typeof y === 'number') {
      return x - y
    }
    if (typeof x === 'number') {
      return -1
    }
    if (typeof y === 'number') {
      return 1
    }
    return x < y ? -1 : 1
  }
  return ka.length - kb.length
}

/** Row-major: a plate is filled A01…A12, B01…, so going back to an earlier well starts a new plate. */
const wellIndex = (well: WellPosition): number => well.row * 12 + well.column

const splitAtWellRestarts = (order: readonly number[], wells: readonly WellPosition[]): number[][] => {
  const plates: number[][] = [[order[0]]]
  for (let i = 1; i < order.length; i++) {
    if (wellIndex(wells[order[i]]) < wellIndex(wells[order[i - 1]])) {
      plates.push([])
    }
    plates[plates.length - 1].push(order[i])
  }
  return plates
}

/**
 * The fewest contiguous blocks of sample numbers, each spanning at most one plate. Among equally few
 * blocks, cuts go at the widest gaps in the numbering (a skipped well, another order's samples), and at
 * the later position when gaps tie. Files with the same number stay together.
 */
const splitBySampleNumbers = (order: readonly number[], numbers: readonly number[]): number[][] => {
  const pos = order.map((i) => numbers[i])
  const n = pos.length
  const best: ([number, number] | null)[] = Array.from({ length: n + 1 }, () => null)
  const prev = new Array<number>(n + 1).fill(-1)
  best[0] = [0, 0]
  for (let j = 1; j <= n; j++) {
    for (let i = j - 1; i >= 0 && pos[j - 1] - pos[i] < PLATE_WELLS; i--) {
      const before = best[i]
      if (before === null || (i > 0 && pos[i] === pos[i - 1])) {
        continue
      }
      const cutCost = i > 0 ? -(pos[i] - pos[i - 1]) * 1e6 - i : 0
      const candidate: [number, number] = [before[0] + 1, before[1] + cutCost]
      const current = best[j]
      if (current === null || candidate[0] < current[0] || (candidate[0] === current[0] && candidate[1] < current[1])) {
        best[j] = candidate
        prev[j] = i
      }
    }
  }
  const plates: number[][] = []
  for (let j = n; j > 0; j = prev[j]) {
    plates.unshift(order.slice(prev[j], j))
  }
  return plates
}

export type PlateVerdict = 'ONE_PLATE' | 'PLATES' | 'UNKNOWN'

export type PlateMethod = 'file-count' | 'wells' | 'sample-numbers'

export type PlateResult = {
  verdict: PlateVerdict
  method: PlateMethod | null
  reason: string
  /** File indexes per plate, in plate order; empty for `UNKNOWN`. */
  plates: number[][]
}

export type PlateOptions = {
  /** Below this many files the order is one plate without looking further. */
  minFilesToSplit: number
  /** The name signals to use, in order of preference. */
  methods: readonly ('wells' | 'sample-numbers')[]
}

/* splitting small orders too wrongly splits 17 instead of 4 of 509 single-pool orders (dominant lab, prod) */
export const DEFAULT_PLATE_OPTIONS: PlateOptions = { minFilesToSplit: PLATE_WELLS + 1, methods: ['wells', 'sample-numbers'] }

/**
 * Suggests plates for an order's forward files. Within each sequencing run, files are ordered by their
 * Illumina sample number (else by name); a restart of the well positions starts a new plate, and without
 * well positions the sample numbers are cut into the fewest blocks of at most 96. `UNKNOWN` when the
 * names carry neither, which is the cue to ask. Measured on only two labs' multi-pool orders, so treat
 * the plates as a suggestion to confirm, not a fact.
 */
export const detectPlates = (files: readonly PlateFile[], options: PlateOptions = DEFAULT_PLATE_OPTIONS): PlateResult => {
  if (files.length === 0) {
    return { verdict: 'UNKNOWN', method: null, reason: 'no files', plates: [] }
  }
  if (files.length < options.minFilesToSplit) {
    return { verdict: 'ONE_PLATE', method: 'file-count', reason: `${files.length} files fit one plate`, plates: [files.map((_, i) => i)] }
  }
  const numbers = files.map((file) => illuminaSampleNumber(file.name))
  const wells = files.map((file) => wellPosition(file.name))
  const runs = new Map<string, number[]>()
  files.forEach((file, i) => {
    const run = file.run ?? fileRuns(file.headers ?? []).keys[0] ?? ''
    runs.set(run, [...(runs.get(run) ?? []), i])
  })
  const ordered = [...runs.values()].map((indexes) =>
    [...indexes].sort((a, b) => (numbers[a] ?? 0) - (numbers[b] ?? 0) || compareNatural(files[a].name, files[b].name))
  )

  const allWells = options.methods.includes('wells') && wells.every((well) => well !== null)
  const allNumbers = options.methods.includes('sample-numbers') && numbers.every((number) => number !== null)
  let plates: number[][]
  let method: PlateMethod
  if (allWells) {
    plates = ordered.flatMap((order) => splitAtWellRestarts(order, wells as WellPosition[]))
    method = 'wells'
  } else if (allNumbers) {
    plates = ordered.flatMap((order) => splitBySampleNumbers(order, numbers as number[]))
    method = 'sample-numbers'
  } else {
    return {
      verdict: 'UNKNOWN',
      method: null,
      reason: 'the file names carry neither well positions nor Illumina sample numbers',
      plates: []
    }
  }
  const how = method === 'wells' ? 'the well positions restart' : 'the sample numbers span more than one plate'
  if (plates.length === 1) {
    return { verdict: 'ONE_PLATE', method, reason: 'one plate: neither a well restart nor a numbering beyond one plate', plates }
  }
  return { verdict: 'PLATES', method, reason: `${plates.length} plates where ${how}`, plates }
}
